import React from "react";
import axios from "axios";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Package } from "lucide-react";
import { OrderStatusBadge, OrderActions } from "./OrderActions";
import { OrderPaymentSummary } from "./OrderPaymentSummary";
import { OrderItemsList } from "./OrderItemsList";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

/**
 * Orders tab — extracted from AdminApp.jsx during refactor.
 * Pure presentational + thin click handlers that delegate to parent.
 */
export const OrdersView = ({
  orders,
  orderFilter,
  packlinkLastSync,
  setPacklinkLastSync,
  syncingPacklink,
  setSyncingPacklink,
  setSuccess,
  setError,
  fetchOrders,
  setShowCreateOrderModal,
  formatDate,
  formatPrice,
  getPaymentMethodLabel,
  shippingRulesAdmin,
  updateOrderStatus,
  handleDuplicateOrder,
}) => {
  // Free-text search across order id, customer name, email, phone, tracking,
  // Packlink reference. Matches as substring (case-insensitive). The input is
  // sticky inside this view so switching tabs preserves the term.
  const [searchTerm, setSearchTerm] = React.useState("");

  const handleSyncPacklink = async () => {
    if (syncingPacklink) return;
    setSyncingPacklink(true);
    try {
      const res = await axios.post(`${API}/packlink/sync-tracking`);
      const { checked, updated, errors, ts, auto_linked } = res.data || {};
      setPacklinkLastSync({
        last_sync_at: ts,
        last_checked: checked,
        last_updated: updated,
        last_errors_count: errors?.length || 0,
      });
      const linked = auto_linked?.linked || 0;
      const linkedMsg = linked > 0 ? ` · 🔗 ${linked} ordine/i agganciato/i a Packlink` : "";
      if (updated > 0 || linked > 0) {
        setSuccess(`✅ Sync Packlink: ${updated} aggiornato/i (su ${checked} controllati)${linkedMsg}`);
        fetchOrders();
      } else if (checked === 0 && linked === 0) {
        setSuccess("Nessuno ordine Packlink da sincronizzare");
      } else {
        setSuccess(`Sync Packlink completato: ${checked} controllati, nessun cambio${linkedMsg}`);
      }
      if (errors?.length) {
        console.warn("Packlink sync errors:", errors);
      }
      setTimeout(() => setSuccess(""), 5000);
    } catch (err) {
      setError(err.response?.data?.detail || "Errore durante la sincronizzazione Packlink");
      setTimeout(() => setError(""), 4000);
    } finally {
      setSyncingPacklink(false);
    }
  };

  const filtered = (() => {
    // Step 1: tab filter (status)
    let list =
      orderFilter === "new"
        ? orders.filter((o) => o.status === "pending")
        : orderFilter === "confirmed"
          ? orders.filter((o) => o.status === "confirmed" || o.status === "processing" || o.status === "partially_shipped")
          : orderFilter === "shipped"
            ? orders.filter((o) => o.status === "shipped")
            : orderFilter === "delivered"
              ? orders.filter((o) => o.status === "delivered")
              : orders;

    // Step 2: free-text search across id, name, email, phone, tracking, ref
    const q = (searchTerm || "").trim().toLowerCase();
    if (q) {
      list = list.filter((o) => {
        const ci = o.customer_info || {};
        const haystack = [
          o.id,
          ci.name,
          ci.email,
          ci.phone,
          o.tracking_number,
          o.packlink_reference,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
    }

    return list.sort((a, b) => new Date(b.order_date) - new Date(a.order_date));
  })();

  const totalCount = filtered.length;

  const headerTitle =
    orderFilter === "new"
      ? "🆕 Nuovi Ordini"
      : orderFilter === "confirmed"
        ? "✅ Ordini da Gestire"
        : orderFilter === "shipped"
          ? "📦 Ordini Spediti"
          : orderFilter === "delivered"
            ? "✓ Ordini Consegnati"
            : "📋 Tutti gli Ordini";

  const formatLastSync = () => {
    if (!packlinkLastSync?.last_sync_at) return null;
    const diffMs = Date.now() - new Date(packlinkLastSync.last_sync_at).getTime();
    const m = Math.floor(diffMs / 60000);
    if (m < 1) return "ora";
    if (m < 60) return `${m} min fa`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} h fa`;
    return new Date(packlinkLastSync.last_sync_at).toLocaleDateString("it-IT");
  };

  const resolveCountry = (order) => {
    if (order.customer_info.specificCountry) return order.customer_info.specificCountry;
    const countryId = order.customer_info.country || order.customer_country || "italia";
    const rule = shippingRulesAdmin.find((r) => r.id === countryId);
    return rule ? rule.country : countryId === "italia" ? "Italia" : countryId;
  };

  return (
    <>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">{headerTitle}</h2>
          <p className="text-gray-600">Totale: {totalCount}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          {packlinkLastSync?.last_sync_at && (
            <p className="text-xs text-gray-500" data-testid="packlink-last-sync-label">
              Ultima sync Packlink:{" "}
              <span className="font-medium text-gray-700">{formatLastSync()}</span>
              {" · "}
              <span>
                {packlinkLastSync.last_updated > 0
                  ? `${packlinkLastSync.last_updated} aggiornati`
                  : "0 cambi"}
              </span>
            </p>
          )}
          <div className="flex gap-2">
            <Button
              onClick={handleSyncPacklink}
              disabled={syncingPacklink}
              variant="outline"
              data-testid="sync-packlink-btn"
            >
              {syncingPacklink ? "Sincronizzando…" : "🔄 Sincronizza Tracking"}
            </Button>
            <Button
              onClick={() => setShowCreateOrderModal(true)}
              className="bg-green-600 hover:bg-green-700"
              data-testid="create-manual-order-btn"
            >
              ➕ Crea Ordine Manuale
            </Button>
          </div>
        </div>
      </div>

      {/* Free-text search bar — filters across order id, customer name,
          email, phone, tracking number and Packlink reference (substring,
          case-insensitive). Sits between the header and the orders list. */}
      <div className="mb-4 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center" data-testid="orders-search-bar">
        <div className="relative flex-1">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="🔍 Cerca per nome, email, telefono, n° ordine o tracking..."
            className="w-full px-4 py-2.5 pr-10 rounded-lg border border-gray-700 bg-[#111118] text-gray-100 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500/60"
            data-testid="orders-search-input"
            autoComplete="off"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200 p-1"
              aria-label="Cancella ricerca"
              data-testid="orders-search-clear"
            >
              ✕
            </button>
          )}
        </div>
        {searchTerm && (
          <span className="text-sm text-gray-400 whitespace-nowrap" data-testid="orders-search-count">
            {filtered.length} {filtered.length === 1 ? "risultato" : "risultati"}
          </span>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12" data-testid="orders-empty">
          <div className="text-gray-400 mb-4">
            <Package className="w-24 h-24 mx-auto" />
          </div>
          <h3 className="text-xl font-medium text-gray-900 mb-2">
            {searchTerm
              ? `Nessun risultato per "${searchTerm}"`
              : orderFilter === "new"
                ? "Nessun nuovo ordine"
                : "Nessun ordine"}
          </h3>
          <p className="text-gray-600 mb-6">
            {searchTerm
              ? "Prova con una parte di nome, email, telefono o numero ordine."
              : orderFilter === "new"
                ? "Non ci sono ordini in attesa di conferma!"
                : orderFilter === "confirmed"
                  ? "Conferma i nuovi ordini per vederli qui."
                  : orderFilter === "shipped"
                    ? "Gli ordini spediti appariranno qui."
                    : orderFilter === "delivered"
                      ? "Gli ordini consegnati appariranno qui."
                      : "Gli ordini ricevuti appariranno qui!"}
          </p>
        </div>
      ) : (
        <div className="space-y-6" data-testid="orders-list">
          {filtered.map((order) => (
            <Card
              key={order.id}
              className="hover:shadow-lg transition-shadow duration-200"
              data-testid={`order-card-${order.id}`}
            >
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3
                        className="text-lg font-semibold text-gray-900"
                        data-testid={`order-id-${order.id}`}
                      >
                        Ordine #{order.id.slice(-8)}
                      </h3>
                      {/* Orange badge highlights eBay-originating orders so the
                          admin can spot them at a glance and route them through
                          the correct shipping / refund flow (eBay payments are
                          settled by eBay, not by our Stripe account). */}
                      {(order.payment_method || "").toLowerCase() === "ebay" && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-orange-100 text-orange-700 border border-orange-300"
                          data-testid={`ebay-badge-${order.id}`}
                          title="Ordine importato da eBay"
                        >
                          🛒 eBay
                        </span>
                      )}
                      {/* Express Checkout badge — Apple Pay / Google Pay / Link
                          skipped the address form; detected by:
                          - session_id starting with `pi_` (PaymentIntent), OR
                          - metadata.order_type === "ecommerce_express" */}
                      {((order.session_id || "").startsWith("pi_") ||
                        (order.metadata?.order_type === "ecommerce_express")) && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-indigo-100 text-indigo-700 border border-indigo-300"
                          data-testid={`express-badge-${order.id}`}
                          title="Ordine tramite Express Checkout (Apple Pay / Google Pay / Link)"
                        >
                          ⚡ Express
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600" data-testid={`order-date-${order.id}`}>
                      {formatDate(order.order_date)}
                    </p>
                  </div>
                  <div className="text-right">
                    <div
                      className="text-xl font-bold text-green-600"
                      data-testid={`order-total-${order.id}`}
                    >
                      {formatPrice(order.total_amount)}
                    </div>
                    <OrderStatusBadge status={order.status} />
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
                  <div data-testid={`order-customer-${order.id}`}>
                    <h4 className="font-medium text-gray-900 mb-2">Cliente</h4>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>
                        <strong>Nome:</strong> {order.customer_info.name}
                      </p>
                      <p>
                        <strong>Email:</strong> {order.customer_info.email}
                      </p>
                      <p>
                        <strong>Telefono:</strong> {order.customer_info.phone}
                      </p>
                      <p>
                        <strong>Indirizzo:</strong> {order.customer_info.address}
                      </p>
                      <p>
                        <strong>Città:</strong> {order.customer_info.city}{" "}
                        {order.customer_info.zipCode || order.customer_info.postal_code || ""}
                      </p>
                      <p>
                        <strong>🌍 Paese:</strong> {resolveCountry(order)}
                      </p>
                      {order.customer_info.notes && (
                        <p>
                          <strong>Note:</strong> {order.customer_info.notes}
                        </p>
                      )}
                    </div>
                  </div>

                  <div data-testid={`order-payment-${order.id}`}>
                    <h4 className="font-medium text-gray-900 mb-2">Dettagli Pagamento</h4>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>
                        <strong>Metodo:</strong> {getPaymentMethodLabel(order.payment_method)}
                      </p>
                      <OrderPaymentSummary order={order} />
                      {(order.coupon_code || order.coupon?.code || (order.discount_amount || 0) > 0) && (
                        <div className="mt-1 p-2 bg-pink-50 border-l-4 border-pink-400 rounded" data-testid={`order-coupon-${order.id}`}>
                          <p className="font-semibold text-pink-800">
                            🏷️ Coupon: <span className="font-mono">{order.coupon_code || order.coupon?.code || "—"}</span>
                          </p>
                          <p className="text-pink-700">
                            Sconto applicato: <strong>−€{(order.discount_amount ?? order.coupon?.discount_amount ?? 0).toFixed(2)}</strong>
                            {order.coupon?.discount_percent ? ` (${order.coupon.discount_percent}%)` : ""}
                            {order.coupon?.free_shipping ? " + spedizione gratis" : ""}
                          </p>
                        </div>
                      )}
                      {order.shipping_method === "inpost" && order.inpost_pickup_point && (
                        <div className="mt-2 p-2 bg-blue-50 border-l-4 border-blue-400 rounded">
                          <p className="font-semibold text-blue-900">📦 InPost:</p>
                          <p className="text-blue-800 text-xs">{order.inpost_pickup_point}</p>
                          {order.inpost_insurance && (
                            <p className="text-blue-800 text-xs">🛡️ Assicurazione inclusa</p>
                          )}
                        </div>
                      )}
                      {order.tracking_number && (
                        <p>
                          <strong>Tracking:</strong> {order.tracking_number}
                        </p>
                      )}

                      {/* Shipping block — visible for any order with a Packlink
                          link or a tracking number. Lets the admin verify
                          courier + tracking at a glance, even on delivered
                          orders (which is the most common admin question:
                          "is this order really shipped?"). */}
                      {(order.packlink_reference || order.tracking_number || order.packlink_carrier) && (
                        <div className="mt-2 p-3 bg-purple-50 border-l-4 border-purple-500 rounded-r" data-testid={`shipping-block-${order.id}`}>
                          <p className="font-semibold text-purple-900 text-sm mb-1">📦 Spedizione</p>
                          {order.packlink_carrier && (
                            <p className="text-purple-900 text-xs">
                              <strong>Corriere:</strong> {order.packlink_carrier}
                            </p>
                          )}
                          {order.tracking_number && (
                            <p className="text-purple-900 text-xs break-all">
                              <strong>Tracking:</strong>{" "}
                              <span className="font-mono">{order.tracking_number}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(order.tracking_number);
                                }}
                                className="ml-2 text-purple-700 hover:underline"
                                title="Copia tracking"
                              >
                                copia
                              </button>
                            </p>
                          )}
                          {order.packlink_status && (
                            <p className="text-purple-900 text-xs">
                              <strong>Stato Packlink:</strong>{" "}
                              <span className="font-mono">{order.packlink_status}</span>
                            </p>
                          )}
                          {order.packlink_reference && (
                            <p className="text-purple-900 text-xs">
                              <strong>Riferimento:</strong>{" "}
                              <a
                                href={`https://pro.packlink.it/private/shipments/${order.packlink_reference}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-purple-700 underline font-mono"
                              >
                                {order.packlink_reference}
                              </a>
                            </p>
                          )}
                          {!order.packlink_carrier && !order.tracking_number && order.packlink_reference && (
                            <p className="text-purple-800 text-xs italic mt-1">
                              Etichetta creata. Tracking sarà disponibile dopo il ritiro del corriere.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <OrderItemsList order={order} formatPrice={formatPrice} />

                <OrderActions
                  order={order}
                  onStatusUpdate={updateOrderStatus}
                  onDuplicate={handleDuplicateOrder}
                  shippingRules={shippingRulesAdmin}
                  onRefresh={fetchOrders}
                />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};
